// BB.3 — Plano de Negócio completo (documento em capítulos).
//
// Rota: /membros/meu-negocio/plano
// Consome getFullBusinessPlanBundle. Não introduz nova fonte canônica.
// Renderiza capítulos com índice lateral (desktop) / recolhível (mobile),
// e suporta impressão via window.print().

import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect } from "react";
import { ArrowLeft, ArrowRight, Printer, AlertTriangle, CheckCircle2, Circle, Compass, Lock } from "lucide-react";
import { getFullBusinessPlanBundle } from "@/lib/business-plan/editor.functions";
import type { Chapter, DocSection, DocStatus, Metric } from "@/lib/business-plan/full-plan";
import { track } from "@/hooks/useAnalytics";
import { HomologacaoObserver } from "@/components/business-builder/HomologacaoObserver";
import { BuilderFeedbackPrompt } from "@/components/business-builder/BuilderFeedbackPrompt";

export const Route = createFileRoute("/_authenticated/membros/meu-negocio/plano")({
  head: () => ({
    meta: [
      { title: "Plano de negócio — Projeto 101" },
      { name: "description", content: "Documento completo do seu negócio, gerado a partir das seções construídas." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PlanoCompleto,
});

const STATUS_LABEL: Record<DocStatus, string> = {
  complete: "Completa", partial: "Em construção", attention: "Precisa revisar",
  blocked: "Bloqueada", empty: "Não iniciada", not_applicable: "Não se aplica",
};

const STATUS_TONE: Record<DocStatus, string> = {
  complete: "bg-[var(--green)]/10 text-[var(--green)]",
  partial: "bg-[var(--navy)]/10 text-[var(--navy)]",
  attention: "bg-amber-100 text-amber-700",
  blocked: "bg-black/10 text-[var(--gray-ink)]",
  empty: "bg-black/5 text-[var(--gray-ink)]",
  not_applicable: "bg-black/5 text-[var(--gray-ink)]",
};

const ORIGIN_LABEL: Record<Metric["origin"], string> = {
  informed: "informado", estimated: "estimativa", calculated: "calculado", incomplete: "incompleto",
};

function PlanoCompleto() {
  const fetchBundle = useServerFn(getFullBusinessPlanBundle);
  const { data, isLoading } = useQuery({
    queryKey: ["full-business-plan"],
    queryFn: () => fetchBundle(),
  });

  if (isLoading) {
    return <div className="mx-auto h-96 max-w-5xl animate-pulse rounded-2xl bg-black/5" />;
  }
  if (!data?.hasProject || !data.fullPlan) {
    return (
      <div className="mx-auto max-w-xl rounded-2xl border border-black/5 bg-white p-8 text-center">
        <h1 className="font-display text-xl font-extrabold text-[var(--navy)]">Sem plano ativo</h1>
        <p className="mt-2 text-sm text-[var(--gray-ink)]">Escolha um negócio na biblioteca para começar.</p>
        <Link to="/membros/negocios" className="mt-5 inline-flex items-center gap-2 rounded-md bg-[var(--navy)] px-4 py-2 text-sm font-semibold text-white">
          Explorar biblioteca <ArrowRight size={14} />
        </Link>
      </div>
    );
  }

  const { fullPlan } = data;
  const pending = fullPlan.pendingItems.length;
  const finished = pending === 0;

  useEffect(() => {
    track("builder_plan_viewed", { metadata: { pending, finished } });
  }, [pending, finished]);

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-break { break-before: page; page-break-before: always; }
          body { background: white !important; }
          html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      <div className="mx-auto max-w-6xl space-y-6 print:max-w-none">
        <header className="rounded-2xl border border-black/5 bg-white p-6 lg:p-8 print:border-0 print:p-0">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="no-print flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
                <Link to="/membros/meu-negocio" className="inline-flex items-center gap-1 hover:text-[var(--navy)]">
                  <ArrowLeft size={11} /> Voltar
                </Link>
                <span>·</span>
                <span>Plano de negócio</span>
              </div>
              <h1 className="mt-1 font-display text-3xl font-extrabold text-[var(--navy)] lg:text-4xl">
                {fullPlan.business?.title ?? "Meu negócio"}
              </h1>
              {fullPlan.business?.methodologyLabel && (
                <p className="mt-1 text-xs text-[var(--gray-ink)]">Metodologia: {fullPlan.business.methodologyLabel}</p>
              )}
              {fullPlan.business?.summary && (
                <p className="mt-2 max-w-3xl text-sm text-[var(--gray-ink)]">{fullPlan.business.summary}</p>
              )}
            </div>
            <div className="no-print flex flex-col gap-2 sm:flex-row">
              {pending > 0 && (
                <Link to="/membros/meu-negocio/pendencias"
                  className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                  <AlertTriangle size={14} /> {pending} pendência{pending > 1 ? "s" : ""}
                </Link>
              )}
              <button onClick={() => window.print()}
                className="inline-flex items-center gap-2 rounded-lg border border-black/10 bg-white px-3 py-2 text-xs font-semibold text-[var(--navy)] hover:bg-black/[0.03]">
                <Printer size={14} /> Imprimir
              </button>
            </div>
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <ProgressBadge label="Concluídas" value={fullPlan.progress.complete} tone="green" />
            <ProgressBadge label="Em construção" value={fullPlan.progress.partial} tone="navy" />
            <ProgressBadge label="Atenção" value={fullPlan.progress.attention} tone="amber" />
            <ProgressBadge label="Bloqueadas" value={fullPlan.progress.blocked} tone="gray" />
            <ProgressBadge label="Não iniciadas" value={fullPlan.progress.empty} tone="gray" />
          </div>
          <div className="mt-3 flex items-center gap-3">
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-[var(--gray-soft)]">
              <div className="h-full bg-[var(--green)]" style={{ width: `${fullPlan.progress.pct}%` }} />
            </div>
            <span className="font-display text-sm font-extrabold text-[var(--navy)]">{fullPlan.progress.pct}%</span>
          </div>
        </header>

        <div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
          <TableOfContents chapters={fullPlan.chapters} />

          <div className="min-w-0 space-y-8 print:space-y-6">
            {fullPlan.chapters.map((chapter, i) => (
              <ChapterView key={chapter.id} chapter={chapter} first={i === 0} />
            ))}
            <p className="no-print pt-4 text-center text-[11px] text-[var(--gray-ink)]">
              Documento gerado a partir das seções preenchidas em {new Date(fullPlan.generatedAt).toLocaleString("pt-BR")}.
            </p>
          </div>
        </div>
      </div>

      {finished && (
        <div className="no-print mx-auto max-w-3xl">
          <BuilderFeedbackPrompt context="plan_completed" autoOpen />
        </div>
      )}
      <HomologacaoObserver sectionKey="plano" />
    </>
  );
}

function TableOfContents({ chapters }: { chapters: Chapter[] }) {
  return (
    <>
      <aside className="no-print hidden lg:block">
        <nav className="sticky top-4 space-y-1 rounded-2xl border border-black/5 bg-white p-4 text-xs">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Índice</p>
          {chapters.map((c) => (
            <a key={c.id} href={`#cap-${c.id}`}
              className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-[var(--navy)] hover:bg-black/[0.04]">
              <span className="truncate">{c.title}</span>
              <StatusDot status={c.status} />
            </a>
          ))}
        </nav>
      </aside>
      <details className="no-print rounded-2xl border border-black/5 bg-white p-3 text-xs lg:hidden">
        <summary className="cursor-pointer font-semibold text-[var(--navy)]">Índice do plano</summary>
        <ul className="mt-2 space-y-1">
          {chapters.map((c) => (
            <li key={c.id}>
              <a href={`#cap-${c.id}`} className="flex items-center justify-between rounded-md px-2 py-1.5 text-[var(--navy)] hover:bg-black/[0.04]">
                <span>{c.title}</span>
                <StatusDot status={c.status} />
              </a>
            </li>
          ))}
        </ul>
      </details>
    </>
  );
}

function StatusDot({ status }: { status: DocStatus }) {
  const cls =
    status === "complete" ? "bg-[var(--green)]" :
    status === "partial" ? "bg-[var(--navy)]" :
    status === "attention" ? "bg-amber-500" :
    status === "blocked" ? "bg-black/40" : "bg-black/20";
  return <span className={`inline-block h-2 w-2 rounded-full ${cls}`} />;
}

function ChapterView({ chapter, first }: { chapter: Chapter; first: boolean }) {
  return (
    <section id={`cap-${chapter.id}`} className={`scroll-mt-6 rounded-2xl border border-black/5 bg-white p-6 lg:p-8 print:rounded-none print:border-0 print:p-0 ${!first ? "print-break" : ""}`}>
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Capítulo</p>
          <h2 className="font-display text-2xl font-extrabold text-[var(--navy)] lg:text-3xl">{chapter.title}</h2>
          <p className="mt-1 max-w-2xl text-sm text-[var(--gray-ink)]">{chapter.intro}</p>
        </div>
        <StatusBadge status={chapter.status} />
      </header>

      <div className="mt-6 space-y-6">
        {chapter.sections.map((s) => <SectionView key={s.key} section={s} />)}
      </div>
    </section>
  );
}

function SectionView({ section }: { section: DocSection }) {
  return (
    <article className="border-t border-black/5 pt-5 first:border-0 first:pt-0">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-lg font-extrabold text-[var(--navy)]">{section.title}</h3>
        <div className="flex items-center gap-2">
          <StatusBadge status={section.status} />
          {section.editorHref && (
            <Link to={section.editorHref.to as any} params={section.editorHref.params as any}
              className="no-print inline-flex items-center gap-1 rounded-lg border border-black/10 bg-white px-2.5 py-1 text-[11px] font-semibold text-[var(--navy)] hover:bg-black/[0.03]">
              {section.status === "complete" ? "Editar" : section.status === "attention" ? "Corrigir" : section.status === "blocked" ? "Resolver" : "Completar"}
              <ArrowRight size={11} />
            </Link>
          )}
        </div>
      </header>

      {section.paragraphs.length > 0 && (
        <div className="mt-3 space-y-2 text-sm leading-relaxed text-[var(--navy)]/90">
          {section.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      )}

      {section.metrics.length > 0 && (
        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {section.metrics.map((m, i) => (
            <div key={i} className="rounded-lg border border-black/5 bg-[var(--beige)]/40 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">{m.label}</p>
              <p className="font-display text-sm font-extrabold text-[var(--navy)]">{m.value}</p>
              <p className="text-[10px] text-[var(--gray-ink)]">{ORIGIN_LABEL[m.origin]}</p>
            </div>
          ))}
        </div>
      )}

      {section.paragraphs.length === 0 && section.status !== "complete" && (
        <p className="mt-3 rounded-lg bg-[var(--gray-soft)]/50 p-3 text-xs text-[var(--gray-ink)]">
          {section.status === "blocked"
            ? section.reason ?? "Esta seção depende de outra ainda em construção."
            : "Nada preenchido ainda nesta seção."}
        </p>
      )}

      {section.gaps.length > 0 && (
        <ul className="mt-3 space-y-1 text-[11px] text-amber-800">
          {section.gaps.map((g, i) => (
            <li key={i} className="flex items-start gap-1.5"><AlertTriangle size={11} className="mt-0.5 shrink-0" /> {g}</li>
          ))}
        </ul>
      )}
    </article>
  );
}

function StatusBadge({ status }: { status: DocStatus }) {
  const Icon = status === "complete" ? CheckCircle2 : status === "partial" ? Compass : status === "attention" ? AlertTriangle : status === "blocked" ? Lock : Circle;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_TONE[status]}`}>
      <Icon size={10} /> {STATUS_LABEL[status]}
    </span>
  );
}

function ProgressBadge({ label, value, tone }: { label: string; value: number; tone: "green" | "navy" | "amber" | "gray" }) {
  const cls =
    tone === "green" ? "bg-[var(--green)]/10 text-[var(--green)]" :
    tone === "navy" ? "bg-[var(--navy)]/10 text-[var(--navy)]" :
    tone === "amber" ? "bg-amber-100 text-amber-700" :
    "bg-black/5 text-[var(--gray-ink)]";
  return (
    <div className={`rounded-lg px-3 py-2 ${cls}`}>
      <p className="font-display text-lg font-extrabold">{value}</p>
      <p className="text-[10px] font-semibold uppercase tracking-wide">{label}</p>
    </div>
  );
}
